import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  SD_849_ATTACHMENT_ROLE_VALUES,
  SD_849_CLAIM_AGE_BUCKETS,
  SD_849_CLAIM_SIGNAL_VALUES,
  SD_849_REFERENCE_STRATEGIES,
  SD_849_DISPOSITION_VALUES,
  SD_849_LABEL_VALUE_SOURCES,
  SD_849_PARSE_FIX_FIELDS,
  SD_849_PARSE_FIX_INSTRUCTION_MAX_LENGTH,
  SD_849_REVIEW_REASON_VALUES,
  SD_849_SIGNAL_VALUES,
  SD_849_STATED_INTENT_VALUES,
  SD_849_UNPLACED_REASON_VALUES,
} from "@erp/shared-types";
import {
  AttachedChildEmailSchema,
  DetectedSupplierSchema,
  Email849ScoreSignalSchema,
} from "./manual-849.contract";

const UUIDSchema = z.uuid();

export const LabelingQueueKindSchema = z.enum(["emails", "matches"]);
export const SD849DispositionSchema = z.enum(SD_849_DISPOSITION_VALUES);
export const SD849AttachmentRoleSchema = z.enum(SD_849_ATTACHMENT_ROLE_VALUES);
export const SD849SignalSchema = z.enum(SD_849_SIGNAL_VALUES);

// ── Queue 1: emails (labels 1 + 2) ──────────────────────────────────────────

/**
 * What the 849 email classifier decided about an email, as Browse all shows it.
 *
 * Null on the item means NOTHING EVER READ THE EMAIL — a state that must stay
 * distinguishable from a recorded `not_849`. Conflating the two is the failure the
 * whole classifier-visibility spec exists to fix; see **Skipped** in
 * `docs/ship-and-debit/CONTEXT.md`.
 */
export const LabelingClassificationSchema = z.object({
  decision: z.enum(["is_849", "not_849", "context_for_849"]),
  band: z.enum(["act", "needs_decision", "ignore"]),
  method: z.enum(["deterministic", "llm", "skipped"]),
  /** Why — the primary signal a reviewer acts on. */
  reason: z.string().nullable(),
  /**
   * P(is_849), or null for a row written before the verdict carried one.
   *
   * **Null is UNKNOWN, never zero.** A row without it was banded on a different
   * quantity, so its `band` is not a claim this schema's consumer may repeat — see
   * `classifierVerdictDisplay` on the frontend.
   */
  probability: z.number().min(0).max(1).nullable(),
  classifiedAt: z.coerce.date(),
});

/**
 * What the Review queue is allowed to say about the classifier's verdict.
 *
 * Just the reason — NOT the full {@link LabelingClassificationSchema} that Browse
 * gets. The queue admits a blind audit sample of confidently-banded emails (#7170),
 * and `band`, `decision` and `probability` would each give one away: an `ignore`
 * row in this queue is an audit item and nothing else. Sending them and declining
 * to render them is not blindness — the values are still on the wire, and a label
 * written by someone who checked is a label about the audit, not about the email.
 *
 * Browse is unaffected and still receives the whole verdict: there the point is
 * arguing with a classification, so seeing it is the entire job.
 */
export const LabelingReviewQueueClassificationSchema = z.object({
  /** Why the classifier said what it did — the signal the reviewer acts on. */
  reason: z.string().nullable(),
});

/**
 * Another mailbox's delivery of the same supplier message (AIA-43): the ingest
 * worker writes one inbound email per watched mailbox, so one message arrives as
 * two or three rows sharing an RFC Message-ID.
 */
export const LabelingMailboxCopySchema = z.object({
  inboundEmailId: UUIDSchema,
  /** The mailbox it landed in; null for a delivery Graph gave none. */
  mailbox: z.string().nullable(),
  receivedAt: z.coerce.date(),
  /** This copy is already on a claim's 849 — the message has been answered. */
  linkedToClaim: z.boolean(),
});

/**
 * One Review-queue ROW — deliberately not a `LabelingEmailItem`.
 *
 * Everything here comes off the candidate query's own page, so a row costs no
 * queries of its own. The full item — body, attachment list, forwarded children —
 * is three more queries PER ROW, and the queue renders up to its cap at once: at
 * the default of 50 that is ~150 concurrent reads to draw a list, and the list
 * does not use any of it. The modal fetches the full item for the one email the
 * reviewer actually opened, which is the only place it was ever needed.
 */
export const LabelingReviewQueueItemSchema = z.object({
  inboundEmailId: UUIDSchema,
  from: z.string(),
  subject: z.string().nullable(),
  receivedAt: z.coerce.date(),
  /**
   * How many attachments the modal will actually list.
   *
   * The DOCUMENT count, not the mailbox row's `attachment_count`: Graph's raw
   * number includes inline signature images, so a row would promise files that
   * are not there. A count the reviewer cannot act on is worse than no count.
   */
  attachmentCount: z.int().nonnegative(),
  detectedSupplier: DetectedSupplierSchema,
  /**
   * The verdict that put this row here, narrowed to what a blind labeller may see.
   *
   * Never null in practice — membership IS a recorded verdict — but nullable so a
   * row can still render if the verdict is deleted between the membership query and
   * this read.
   */
  classification: LabelingReviewQueueClassificationSchema.nullable(),
  /**
   * How many mailbox deliveries this one item stands for, itself included
   * (AIA-43). The queue shows one item per message; the review page lists the
   * other copies.
   */
  copyCount: z.int().positive().default(1),
});

export const LabelingAttachmentSchema = z.object({
  attachmentId: UUIDSchema,
  fileName: z.string(),
  mimeType: z.string(),
  /** Null when the document store never recorded one. */
  sizeBytes: z.number().nullable(),
  /** Pre-filled guess the expert confirms or corrects. */
  suggestedRole: SD849AttachmentRoleSchema,
  /**
   * The zip on this email the file was taken out of, when a reviewer unzipped
   * it on the review page; null for a file the supplier attached directly.
   */
  fromArchive: z
    .object({ attachmentId: UUIDSchema, fileName: z.string() })
    .nullable()
    .default(null),
});

// ── Zip attachments: list a zip's files, take the related ones out ──────────

/** Most files one "unzip" may take out of an archive. */
export const SD_849_ARCHIVE_MAX_EXTRACT_FILES = 50;

/** Why a file in a zip cannot be taken out. */
export const SD849ArchiveMemberUnavailableReasonSchema = z.enum([
  /** Password-protected; nothing here can read it. */
  "encrypted",
  /** Bigger than any supplier report has reason to be. */
  "too_large",
]);

export const EmailArchiveMemberSchema = z.object({
  /** The file's path inside the zip — how an extract request names it. */
  path: z.string(),
  /** The name it has (or will have) as an attachment of the email. */
  fileName: z.string(),
  /** Uncompressed size, as the zip declares it. */
  sizeBytes: z.int().nonnegative(),
  unavailableReason: SD849ArchiveMemberUnavailableReasonSchema.nullable(),
  /** The email attachment it already became, when it was taken out before. */
  attachmentId: UUIDSchema.nullable(),
});

export const EmailArchiveListingSchema = z.object({
  members: z.array(EmailArchiveMemberSchema),
  /** The zip holds more files than one listing shows. */
  truncated: z.boolean(),
});

export const EmailArchiveTargetSchema = z.object({
  inboundEmailId: UUIDSchema,
  /** The zip attachment. */
  attachmentId: UUIDSchema,
});

export const ExtractEmailArchiveMembersInputSchema =
  EmailArchiveTargetSchema.extend({
    /** The files the reviewer picked, by their path inside the zip. */
    paths: z
      .array(z.string().min(1))
      .min(1)
      .max(SD_849_ARCHIVE_MAX_EXTRACT_FILES),
  });

export const ExtractEmailArchiveMembersOutputSchema = z.object({
  /** Every picked file and the attachment it is now — new or from before. */
  attachments: z.array(
    z.object({ path: z.string(), attachmentId: UUIDSchema }),
  ),
  /** How many of them this request created. */
  createdCount: z.int().nonnegative(),
});

export const LabelingEmailItemSchema = z.object({
  inboundEmailId: UUIDSchema,
  from: z.string(),
  /** Recipient(s). Usually the S&D mailbox, but a forward or an alias shows up
   *  here and changes how the message should be read. */
  to: z.string(),
  subject: z.string().nullable(),
  receivedAt: z.coerce.date(),
  /** Auto-deduced from the sender — the expert confirms rather than fills it in. */
  detectedSupplier: DetectedSupplierSchema,
  /** The engine's belief + the named signals that produced it, so a confirm is an
   *  informed click rather than a rubber stamp. */
  confidence: z.object({
    isAn849: z.number().min(0).max(1),
    relatedToExisting: z.number().min(0).max(1),
    signals: z.array(Email849ScoreSignalSchema),
  }),
  /** Every 849 this email is on — one per claim it answers — oldest first. */
  existingResponseIds: z.array(UUIDSchema),
  /** First few hundred characters of the body, whitespace-collapsed — enough to
   *  judge most emails without a second request. Full body is fetched on demand. */
  bodyPreview: z.string().nullable(),
  attachments: z.array(LabelingAttachmentSchema),
  /**
   * Emails forwarded onto this one as attachments.
   *
   * Usually empty. When it isn't, these are the messages the reviewer was told "did
   * not come through": the ingest worker files them as their own inbound emails, so
   * they are not documents on this email and can never be a queue row of their own.
   * Same shape the 849 inbox uses — see {@link AttachedChildEmailSchema}.
   */
  attachedEmails: z.array(AttachedChildEmailSchema).default([]),
  /**
   * The same message as delivered to our other mailboxes, oldest first (AIA-43).
   * Empty when it arrived once or carries no Message-ID.
   */
  copies: z.array(LabelingMailboxCopySchema).default([]),
  /**
   * What the classifier decided about this email.
   *
   * Set on Review-queue rows, where the recorded REASON is the primary signal the
   * reviewer acts on — they are experts, and a percentage tells them nothing they
   * can argue with. Null elsewhere, and null means NOTHING EVER CLASSIFIED IT, which
   * is a different state from a recorded `not_849`.
   */
  classification: LabelingClassificationSchema.nullable().default(null),
  /**
   * What was already labeled for this email, when re-opening one.
   *
   * Null in the Review queue (its items are unlabeled by definition) and set by the
   * detail endpoint. The form must prefer this over detection — Browse lists labeled
   * emails on purpose, so re-opening one is normal, and initialising from detection
   * would let a confirm overwrite the expert's supplier with the detector's guess.
   */
  savedLabel: z
    .object({
      /** Null when only the supplier was saved — it saves as soon as it is picked. */
      disposition: SD849DispositionSchema.nullable(),
      supplierId: UUIDSchema.nullable(),
      /**
       * The saved supplier's display name. The stored id is an `ap_suppliers.id`,
       * which the picker (keyed by company) cannot name on its own.
       */
      supplierName: z.string().nullable().optional(),
      labeledAt: z.coerce.date(),
    })
    .nullable()
    .optional(),
});

/** The engine's prediction at label time — stored so each label is a truth pair. */
export const LabelingPredictionSchema = z.object({
  detectedSupplierId: UUIDSchema.nullable().optional(),
  detectedSupplierName: z.string().nullable().optional(),
  supplierConfidence: z.enum(["resolved", "unknown"]).optional(),
  hasParseConfig: z.boolean().optional(),
  isAn849Score: z.number().optional(),
});

/**
 * Where a submitted value came from — the instrument that makes a label readable.
 *
 * Without it a pre-filled answer and a deliberate one are the same row, so "the
 * reviewer judged the body irrelevant" is indistinguishable from "the reviewer never
 * looked at it". Every source field is OPTIONAL: a client that sends none records
 * `null`, which must stay readable as "unknown" rather than "user". Historical rows
 * are not backfilled — the ambiguity there is unrecoverable.
 */
const SD849LabelValueSourceSchema = z.enum(SD_849_LABEL_VALUE_SOURCES);
/** A disposition is always a click; there is nothing to inherit. */
export const SD849DispositionSourceSchema = SD849LabelValueSourceSchema.extract(
  ["user"],
);
/** `unanswered` = the control was never touched and the default was submitted. */
export const SD849BodyRoleSourceSchema = SD849LabelValueSourceSchema.extract([
  "user",
  "unanswered",
]);
/** `prefilled` = the heuristic's guess went in untouched. */
export const SD849AttachmentRoleSourceSchema =
  SD849LabelValueSourceSchema.extract(["user", "prefilled"]);
/** `prefilled` = the detector's supplier went in untouched. */
export const SD849SupplierSourceSchema = SD849LabelValueSourceSchema.extract([
  "user",
  "prefilled",
]);

export const SubmitEmailLabelInputSchema = z.object({
  inboundEmailId: UUIDSchema,
  disposition: SD849DispositionSchema,
  /** Always a click today; recorded anyway so the field reads uniformly. */
  dispositionSource: SD849DispositionSourceSchema.optional(),
  /** Required by the UI when disposition is `is_849` — it's half of completeness. */
  supplierId: UUIDSchema.nullable().optional(),
  supplierName: z.string().nullable().optional(),
  /**
   * Whether the reviewer picked the supplier or kept the detector's guess.
   *
   * `agreed` already says the submitted id MATCHES detection — it cannot say
   * whether anyone looked. Keeping the guess and deliberately re-picking the same
   * company submit the identical id, and they are not the same evidence.
   */
  supplierSource: SD849SupplierSourceSchema.optional(),
  attachmentRoles: z
    .array(
      z.object({
        attachmentId: UUIDSchema,
        role: SD849AttachmentRoleSchema,
        suggestedRole: SD849AttachmentRoleSchema.nullable().optional(),
        roleSource: SD849AttachmentRoleSourceSchema.optional(),
      }),
    )
    .optional(),
  /**
   * The message body's role, exactly like an attachment's.
   *
   * The body is just another part of the email that may or may not carry the
   * supplier's decision, so it takes the same three-way answer rather than a boolean
   * — "supporting" (context around a decision that's attached) is a real state a
   * checkbox couldn't express.
   */
  bodyRole: SD849AttachmentRoleSchema.optional(),
  /**
   * What the form pre-filled the body role with.
   *
   * The body used to send no prediction at all, which made its `agreed` metadata
   * permanently `null` — the one part of the email whose label taught us nothing.
   */
  bodySuggestedRole: SD849AttachmentRoleSchema.nullable().optional(),
  bodyRoleSource: SD849BodyRoleSourceSchema.optional(),
  /** @deprecated Superseded by `bodyRole`; still honoured for older clients. */
  bodyIsPayload: z.boolean().optional(),
  /**
   * Which signal(s) decided it.
   *
   * No longer asked directly — the UI stopped posing a question the part ROLES
   * already answer. Left on the contract because the backend DERIVES it from those
   * roles, and an explicit value still wins if a caller supplies one.
   */
  signals: z.array(SD849SignalSchema).optional(),
  prediction: LabelingPredictionSchema.optional(),
});

// ── Review session (#7644): one email, the reviewer's whole answer ──────────

export const SD849ReviewReasonSchema = z.enum(SD_849_REVIEW_REASON_VALUES);

/** Most claims one email may be linked to in a session. */
export const SD_849_REVIEW_MAX_CLAIMS = 50;

/** Longest note a reviewer may attach to a verdict. */
export const SD_849_REVIEW_NOTE_MAX_LENGTH = 2000;

// ── Stated decisions (#7651): the supplier's decision, as a reviewer read it ─

export const SD849StatedIntentSchema = z.enum(SD_849_STATED_INTENT_VALUES);

/** Longest reason a reviewer may give for a stated decision. */
export const SD_849_STATED_REASON_MAX_LENGTH = 500;

/** Most per-line credits one statement may carry. */
export const SD_849_STATED_MAX_LINE_CREDITS = 2000;

/** A non-negative amount with at most two decimals, as typed. */
export const StatedAmountSchema = z
  .string()
  .trim()
  .regex(
    /^\d{1,13}(\.\d{1,2})?$/,
    "Must be an amount with at most two decimals",
  );

const StatedReasonSchema = z
  .string()
  .trim()
  .min(1)
  .max(SD_849_STATED_REASON_MAX_LENGTH);

/** What the supplier credited on one line of a linked claim. */
export const StatedLineCreditSchema = z.object({
  submissionId: UUIDSchema,
  sdE844dId: UUIDSchema,
  creditedAmount: StatedAmountSchema,
  /** Falls back to the statement's reason. */
  reason: StatedReasonSchema.optional(),
});

/**
 * The supplier's decision as the reviewer states it (#7651) — for a body-only
 * reply, or figures the parser cannot read. `partial` carries the per-line
 * credits; the whole intents carry none. Applied on the linked claims' still-open
 * lines through the shared disposition rule, with the reviewer as the lines'
 * determining source. A checksum mismatch never blocks it; it is recorded.
 */
export const StatedDecisionInputSchema = z.object({
  intent: SD849StatedIntentSchema,
  reason: StatedReasonSchema,
  lineCredits: z
    .array(StatedLineCreditSchema)
    .max(SD_849_STATED_MAX_LINE_CREDITS)
    .default([]),
});

export const StatedChecksumTotalsSchema = z.object({
  claimed: z.string(),
  credited: z.string().nullable(),
  denied: z.string().nullable(),
});

/**
 * The claims after the statement (`stated`) against the email's own totals
 * (`email`): the report's sums when it carries a readable report, else what we
 * claimed on the linked claims (`source: "claims"`, no credited figure).
 *
 * `no_supplier_figures`: no report row states both a claim and a credit (a
 * body-only reply, or a report whose rows carry neither), so the supplier's
 * figures cannot confirm the statement — it covers what we
 * claimed, and that is all that can be checked. Never shown as balanced.
 *
 * `source: "body"`: no readable report, but the reply's own text states the
 * approved amount ("$29,526.17 is approved for … VDM 35272") — that figure is
 * the supplier's credited total, so the statement can balance against it.
 */
export const StatedChecksumSchema = z.object({
  status: z.enum(["balanced", "mismatch", "no_supplier_figures"]),
  source: z.enum(["report", "claims", "body"]),
  stated: StatedChecksumTotalsSchema,
  email: StatedChecksumTotalsSchema,
  tolerance: z.string(),
});

export const StatedLineDecisionSchema = z.object({
  submissionId: UUIDSchema,
  sdE844dId: UUIDSchema,
  disposition: z.enum(["accepted", "rejected", "partial"]),
  claimedAmount: z.string(),
  /** Exactly what is booked: the supplier's credit, never topped up. */
  acceptedAmount: z.string(),
  reason: z.string(),
});

export const StatedLineSkipSchema = z.object({
  submissionId: UUIDSchema,
  sdE844dId: UUIDSchema,
  /**
   * `claim_record_not_email`: the claim's 849 came from EDI, or the claim has
   * several live 849s — the review never decides lines there.
   */
  reason: z.enum([
    "line_already_settled",
    "not_on_linked_claims",
    "claim_record_not_email",
  ]),
});

/** Why a statement cannot be applied as written. */
export const StatedDecisionViolationSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("partial_needs_credits") }),
  z.object({ kind: z.literal("credits_need_partial") }),
  z.object({ kind: z.literal("duplicate_line"), sdE844dId: UUIDSchema }),
  z.object({ kind: z.literal("credit_unreadable"), sdE844dId: UUIDSchema }),
  // Now only a negative credit (an over-credit is accepted, AIA-44); the name
  // and `claimedAmount` stay for contract stability.
  z.object({
    kind: z.literal("credit_out_of_range"),
    sdE844dId: UUIDSchema,
    claimedAmount: z.string(),
  }),
]);

export const PreviewStatedDecisionInputSchema = z.object({
  inboundEmailId: UUIDSchema,
  /** The supplier on the form — a `co_companies.id` or an `ap_suppliers.id`. */
  supplierId: UUIDSchema,
  submissionIds: z.array(UUIDSchema).min(1).max(50),
  statedDecision: StatedDecisionInputSchema,
  attachmentRoles: z
    .array(
      z.object({ attachmentId: UUIDSchema, role: SD849AttachmentRoleSchema }),
    )
    .optional(),
  /** The body's stated role; a payload body reads no unmarked attachment (#7647). */
  bodyRole: SD849AttachmentRoleSchema.optional(),
});

/**
 * The claims ticked in the claim panel (#7648): the parse view asks which
 * configuration the claim link will read the report with on them.
 */
export const PreviewClaimLinkConfigurationInputSchema = z.object({
  inboundEmailId: UUIDSchema,
  /** The supplier on the form — a `co_companies.id` or an `ap_suppliers.id`. */
  supplierId: UUIDSchema,
  submissionIds: z.array(UUIDSchema).min(1).max(50),
  attachmentRoles: z
    .array(
      z.object({ attachmentId: UUIDSchema, role: SD849AttachmentRoleSchema }),
    )
    .optional(),
  /** The body's stated role; a payload body reads no unmarked attachment (#7647). */
  bodyRole: SD849AttachmentRoleSchema.optional(),
});

/**
 * The configuration the claim link will apply on the ticked claims — the one
 * that works, else the best partial (#7648). Null when the supplier has none.
 */
export const ClaimLinkConfigurationSchema = z.object({
  configuration: z
    .object({
      configId: z.string(),
      label: z.string(),
      versionNumber: z.number().int().nullable(),
    })
    .nullable(),
});

/** One line of a linked claim, for the per-line credits form. */
export const StatedClaimLineSchema = z.object({
  submissionId: UUIDSchema,
  sdE844dId: UUIDSchema,
  invoiceNumber: z.string().nullable(),
  productCode: z.string().nullable(),
  claimedAmount: z.string(),
  open: z.boolean(),
  lineStatus: z.string(),
  acceptedAmount: z.string().nullable(),
});

/**
 * What the statement WOULD do, before the reviewer confirms: the decisions, the
 * lines it leaves alone, anything that stops it applying, and the checksum.
 * READ-ONLY.
 */
export const StatedDecisionPreviewSchema = z.object({
  lines: z.array(StatedClaimLineSchema),
  decisions: z.array(StatedLineDecisionSchema),
  skipped: z.array(StatedLineSkipSchema),
  violations: z.array(StatedDecisionViolationSchema),
  checksum: StatedChecksumSchema,
});

// ── Parse fixes (#7649): a correction to how a report is read ──────────────

export const SD849ParseFixFieldSchema = z.enum(SD_849_PARSE_FIX_FIELDS);

/**
 * A change to ONE sheet of a supplier configuration — the only thing a parse fix
 * can express. A column pick and a plain-text fix both become one: the pick
 * deterministically, the text through the drafting agent. It reaches the
 * report's columns and rows and nothing else: never sender detection,
 * classification, the reconcile shape, the match key or a tolerance.
 *
 *  - `sheetIndex`: which of the base configuration's sheets changes;
 *  - `sheetName` / `headerRow` / `dataStartRow`: null keeps the base's value
 *    (rows are 0-indexed). A stated `headerRow` replaces header auto-detection;
 *  - `columns`: per field, the column it is read from — a header name (or a
 *    column letter, when the sheet maps by letter) — or null to stop reading it;
 *  - `skipRowsWhere`: rows to skip, ADDED to the sheet's own (a totals row).
 */
export const Sd849SheetPatchSchema = z.object({
  sheetIndex: z.int().min(0).max(20),
  sheetName: z.string().min(1).max(200).nullable(),
  headerRow: z.int().min(0).max(200).nullable(),
  dataStartRow: z.int().min(0).max(500).nullable(),
  columns: z
    .array(
      z.object({
        field: SD849ParseFixFieldSchema,
        source: z.string().min(1).max(200).nullable(),
      }),
    )
    .max(SD_849_PARSE_FIX_FIELDS.length),
  skipRowsWhere: z
    .array(
      z.object({
        column: z.string().min(1).max(200),
        contains: z.array(z.string().min(1).max(200)).min(1).max(10),
      }),
    )
    .max(5),
});

/** Which column a field is read from, picked by position in the raw sheet. */
export const ParseColumnPickSchema = z.object({
  field: SD849ParseFixFieldSchema,
  /** Null: the field is read from no column. */
  columnIndex: z.int().nonnegative().nullable(),
});

/** What the reviewer asks for: column picks on one sheet, or a plain-text fix. */
export const ParseFixRequestSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("columns"),
    attachmentId: UUIDSchema,
    sheetName: z.string().min(1),
    picks: z
      .array(ParseColumnPickSchema)
      .min(1)
      .max(SD_849_PARSE_FIX_FIELDS.length),
  }),
  z.object({
    kind: z.literal("instruction"),
    instruction: z
      .string()
      .trim()
      .min(1)
      .max(SD_849_PARSE_FIX_INSTRUCTION_MAX_LENGTH),
  }),
]);

/**
 * The parse fix a session applies: the previewed patch on the configuration it
 * corrects. The session re-applies the patch to that configuration, validates
 * it, dry-runs it on the email, and saves the result as a NEW configuration at
 * the bottom of the ranking — the base is never edited.
 */
export const SessionParseFixSchema = z.object({
  baseConfigId: UUIDSchema,
  source: z.enum(["columns", "instruction"]),
  /** The reviewer's words, kept with the new configuration as its evidence. */
  instruction: z
    .string()
    .trim()
    .max(SD_849_PARSE_FIX_INSTRUCTION_MAX_LENGTH)
    .optional(),
  patch: Sd849SheetPatchSchema,
});

/**
 * A reviewer's session for one email: the email label plus WHY.
 *
 * A superset of {@link SubmitEmailLabelInputSchema} so the same form produces
 * both. `reasons` is optional on the wire, but the server refuses a `not_849` or
 * `context_for_849` without at least one (`sd849ReviewReasonsRequired`) — the
 * form enforces it too, and the server is where it is an invariant.
 *
 * Later tickets of spec #7634 add the claim link, payload roles and parse fixes
 * to this same input; this one carries the verdict, its reasons and the supplier.
 */
export const SubmitReviewSessionInputSchema =
  SubmitEmailLabelInputSchema.extend({
    reasons: z
      .array(SD849ReviewReasonSchema)
      .max(SD_849_REVIEW_REASON_VALUES.length)
      .optional(),
    note: z.string().trim().max(SD_849_REVIEW_NOTE_MAX_LENGTH).optional(),
    /**
     * The claims this email answers (#7645), picked in the claim panel. Absent
     * means the reviewer was not asked; an EMPTY list is the explicit answer
     * "none of the supplier's claims" and creates no 849. An `is_849` links as
     * the claims' decision; a `context_for_849` (#7656) joins the claims' 849
     * responses as context and decides no line. A `not_849` carries none.
     */
    claimLink: z
      .object({
        /** The supplier the claims were listed under (company or supplier id). */
        supplierId: UUIDSchema,
        submissionIds: z.array(UUIDSchema).max(SD_849_REVIEW_MAX_CLAIMS),
      })
      .optional(),
    /**
     * The supplier's decision as the reviewer states it (#7651). Needs a
     * non-empty `claimLink` on an `is_849`: it decides the linked claims'
     * still-open lines after the report's rows are placed.
     */
    statedDecision: StatedDecisionInputSchema.optional(),
    /**
     * A correction to how the report is read (#7649): it becomes a new supplier
     * configuration, entered at the bottom of the ranking, and the claim link
     * reads this email with it. Needs an `is_849` with a supplier; not
     * combined with a stated decision.
     */
    parseFix: SessionParseFixSchema.optional(),
  });

/**
 * Why a rejection-detail attach found no confident targets (SD-6): the subject
 * matches no configured pattern, names no batch, names a batch without exactly
 * one EDI 849, or the email carries no readable workbook.
 */
export const RejectionDetailNotConfidentReasonSchema = z.enum([
  "subject_mismatch",
  "no_batch",
  "batch_unresolved",
  "no_workbook",
]);

/** The line key a workbook row states, as written. */
const RejectionDetailRowKeySchema = z.object({
  batch: z.string().nullable(),
  invoiceNumber: z.string().nullable(),
  supplierItemCode: z.string().nullable(),
});

export const PreviewRejectionDetailInputSchema = z.object({
  inboundEmailId: UUIDSchema,
  /** The supplier on the form — a `co_companies.id` or an `ap_suppliers.id`. */
  supplierId: UUIDSchema,
});

/**
 * Where a rejection-detail supplier's email would land (SD-10): the attach
 * service in preview mode — the same rules as the attach itself — and nothing
 * written. `not_configured`: the supplier has no rejection-detail option.
 * `not_confident`: no targets, so the attach is refused.
 */
export const RejectionDetailPreviewSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("not_configured") }),
  z.object({
    status: z.literal("not_confident"),
    reason: RejectionDetailNotConfidentReasonSchema,
    unresolvedBatches: z.array(z.string()),
  }),
  z.object({
    status: z.literal("previewed"),
    targets: z.array(
      z.object({ batchKey: z.string(), responseId: UUIDSchema }),
    ),
    /** Rows that land on an EDI 849 line, with the detail they add. */
    writes: z.array(
      z.object({
        sheetName: z.string(),
        rowNumber: z.int(),
        row: RejectionDetailRowKeySchema,
        text: z.string(),
        responseId: UUIDSchema,
        lineIndex: z.int().nonnegative(),
      }),
    ),
    /** Rows placed nowhere, and why. */
    unattached: z.array(
      z.object({
        sheetName: z.string(),
        rowNumber: z.int(),
        row: RejectionDetailRowKeySchema,
        text: z.string().nullable(),
        /** `superseded`: the line already holds a newer email's detail (SD-11). */
        reason: z.enum([
          "no_candidate",
          "ambiguous",
          "no_detail",
          "superseded",
        ]),
      }),
    ),
  }),
]);

/**
 * A change the session made to a record — as opposed to a label it wrote.
 *
 * `supplier`: an existing 849 for this email was re-pointed to the reviewer's
 * supplier (and re-parsed under its configuration) through the reconcile engine's
 * correction verb. Absent when the email has no 849 yet, or its 849 already
 * named that supplier: then nothing changed and the supplier is only recorded.
 */
export const ReviewSessionAppliedSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("supplier"),
    responseId: UUIDSchema,
    supplierId: UUIDSchema,
    supplierName: z.string().nullable(),
  }),
  /**
   * `claim-link` (#7645): the email is on this claim's 849 response — `created`
   * for it, `attached` to the one it had, or `already-linked`. `decidedLineCount`
   * is how many of the claim's lines the report decided with this email as the
   * determining source. `matching: "skipped_not_email"` means the claim's record
   * came from EDI, which the email review never decides lines on.
   * `link: "ambiguous-response"` means the claim already carries several live
   * 849s: nothing was linked or decided (`responseId` null, `matching:
   * "skipped_ambiguous"`) — which record it belongs on is for a person.
   * `link: "not-answered"` means no report row is about the claim, so no 849
   * was opened on it (`responseId` null, `matching: "skipped_not_answered"`).
   * `link: "duplicate-copy"` (AIA-43) means the claim's 849 already holds
   * another mailbox's copy of this email: recorded on it, not read again
   * (`matching: "skipped_duplicate_copy"`, nothing decided).
   */
  z.object({
    kind: z.literal("claim-link"),
    submissionId: UUIDSchema,
    responseId: UUIDSchema.nullable(),
    link: z.enum([
      "created",
      "attached",
      "already-linked",
      "ambiguous-response",
      "not-answered",
      "duplicate-copy",
    ]),
    decidedLineCount: z.number().int(),
    /**
     * Of those, how many replaced an earlier decision — one a person stated, or
     * an earlier email's (#7651). Each is listed on the claim's history.
     */
    supersededLineCount: z.number().int(),
    matching: z.enum([
      "applied",
      "skipped_not_email",
      "skipped_ambiguous",
      "skipped_not_answered",
      "skipped_duplicate_copy",
    ]),
  }),
  /**
   * `context-link` (#7656): a `context_for_849` email joined this claim's 849
   * response as context — `created` (the claim had none; the record opens with
   * no anchor and no decided line), `attached`, or `already-linked`. It reads no
   * report and decides no line. `ambiguous-response`: the claim carries several
   * live 849s, and nothing was linked.
   */
  z.object({
    kind: z.literal("context-link"),
    submissionId: UUIDSchema,
    responseId: UUIDSchema.nullable(),
    link: z.enum([
      "created",
      "attached",
      "already-linked",
      "ambiguous-response",
    ]),
  }),
  /**
   * `rejection-detail` (SD-6, ADR 0012): the supplier's configuration marks its
   * emails as rejection-detail-only, so the context email went to the EDI 849s
   * its subject's batches name (`responseIds`), adding Rejection detail to
   * `detailLineCount` lines; `unattachedRowCount` rows placed nowhere. It opens
   * no 849 and decides nothing. `not_confident` (with `reason`) wrote nothing.
   */
  z.object({
    kind: z.literal("rejection-detail"),
    status: z.enum(["applied", "not_confident"]),
    responseIds: z.array(UUIDSchema),
    detailLineCount: z.number().int(),
    unattachedRowCount: z.number().int(),
    reason: RejectionDetailNotConfidentReasonSchema.nullable(),
  }),
  /**
   * `configuration` (#7649): the parse fix saved as a new configuration of the
   * supplier — version 1, active, without sender detection. The configuration
   * it corrects is untouched.
   */
  z.object({
    kind: z.literal("configuration"),
    configId: UUIDSchema,
    label: z.string(),
    baseConfigId: UUIDSchema,
    source: z.enum(["columns", "instruction"]),
    /** Rows it read from this email, in the dry run before it was saved. */
    rowCount: z.number().int(),
  }),
]);

/** A label the session wrote. */
export const ReviewSessionRecordedSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("verdict"),
    disposition: SD849DispositionSchema,
    reasons: z.array(SD849ReviewReasonSchema),
    note: z.string().nullable(),
    /**
     * The verdict overturned a confident classifier (AIA-38): ask the reviewer
     * for a one-line note now, through `saveReviewNote`. Decided after the
     * verdict was written, so the form never showed which way it leaned.
     * Absent means no.
     */
    askNote: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal("supplier"),
    supplierId: UUIDSchema,
    supplierName: z.string().nullable(),
    /** The reviewer kept what the engine detected. */
    agreedWithEngine: z.boolean(),
  }),
  /** The claims the reviewer named; empty is "none of the supplier's claims". */
  z.object({
    kind: z.literal("claims"),
    submissionIds: z.array(UUIDSchema),
  }),
  /**
   * The parts' roles (#7647), recorded as labels. `reportAttachmentIds` are the
   * attachments these roles allow to be read as the report — never a
   * supporting or irrelevant one.
   */
  z.object({
    kind: z.literal("parts"),
    attachmentRoles: z.array(
      z.object({ attachmentId: UUIDSchema, role: SD849AttachmentRoleSchema }),
    ),
    bodyRole: SD849AttachmentRoleSchema.nullable(),
    reportAttachmentIds: z.array(UUIDSchema),
  }),
]);

/**
 * The reviewer's stated decision (#7651), when the session stated one: the
 * statement with the checksum as it stood when it was applied — a mismatch is
 * recorded, never a reason to refuse — the lines it left alone, and, per
 * claim's 849 response, the lines it decided with the reviewer as their
 * determining source.
 *
 * Its own field rather than new `applied`/`recorded` kinds, so the outcome
 * contract only grows (a new response union member is a breaking change).
 */
export const ReviewSessionStatedDecisionSchema = z.object({
  intent: SD849StatedIntentSchema,
  reason: z.string(),
  checksum: StatedChecksumSchema,
  skipped: z.array(StatedLineSkipSchema),
  applied: z.array(
    z.object({
      submissionId: UUIDSchema,
      responseId: UUIDSchema,
      decisions: z.array(StatedLineDecisionSchema),
    }),
  ),
});

/**
 * What the system now knows because of this session.
 *
 * `sender`: the email's sender domain → supplier mapping, read back from the
 * learned-sender map AFTER the writes, so it reports what detection will actually
 * do on the next email rather than what the session hoped. `status` says why a
 * mapping did not take: the domain is ours (`internal_sender`), or the team's
 * answers for it disagree (`contradicted` — a shared domain never becomes a
 * learned sender).
 */
export const LearnedSenderStatusSchema = z.enum([
  "learned",
  "contradicted",
  "internal_sender",
]);

export const ReviewSessionLearnedSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("sender"),
    domain: z.string(),
    supplierName: z.string().nullable(),
    status: LearnedSenderStatusSchema,
  }),
  /**
   * A classification rule the team's reasons drafted or retired (#7653), in
   * effect from the next email with no activation click. `noise` keeps its shape
   * out of the review queue (the emails already there leave it in the
   * background, after the session returns); `subject` is a
   * supplier rule the classifier tries after the configured ones. `retired`: a
   * later label contradicted it, and it no longer applies. `agreeing` /
   * `contradicting` are the dry run against every labelled email.
   */
  z.object({
    kind: z.literal("rule"),
    change: z.enum(["entered", "retired"]),
    ruleKind: z.enum(["noise", "subject"]),
    verdict: SD849DispositionSchema,
    reason: SD849ReviewReasonSchema,
    senderDomain: z.string(),
    subjectPrefix: z.string().nullable(),
    agreeing: z.number().int(),
    contradicting: z.number().int(),
  }),
  /**
   * `configuration` (#7649): where the ranking now puts the configuration the
   * parse fix produced, and the one it corrected — which records a failure on
   * this email. Positions are 1-based among the supplier's configurations.
   */
  z.object({
    kind: z.literal("configuration"),
    configId: UUIDSchema,
    label: z.string(),
    position: z.number().int(),
    of: z.number().int(),
    demotedConfigId: UUIDSchema,
    demotedLabel: z.string(),
    demotedPosition: z.number().int().nullable(),
  }),
  /**
   * `reference` (#7650): what a claim link taught the supplier's reference
   * configurations — where it writes our VDM numbers. `entered`: the email was
   * unreferenced and this strategy would have read it; it joins at the bottom
   * of the ranking. `failed`: the strategy named a claim the reviewer did not
   * link. Positions are 1-based among the supplier's reference configurations.
   */
  z.object({
    kind: z.literal("reference"),
    change: z.enum(["entered", "failed"]),
    strategy: z.enum(SD_849_REFERENCE_STRATEGIES),
    position: z.number().int(),
    of: z.number().int(),
  }),
]);

export const SD849UnplacedReasonSchema = z.enum(SD_849_UNPLACED_REASON_VALUES);

/**
 * A report row the claim link could not place (#7645). Its line stays pending;
 * the reason says what would have to be settled to place it. `submissionId` is
 * the one claim the row belongs to, when that much is known.
 */
export const ReviewSessionUnplacedRowSchema = z.object({
  rowIndex: z.number().int(),
  reason: SD849UnplacedReasonSchema,
  submissionId: UUIDSchema.nullable(),
  invoiceNumber: z.string().nullable(),
  productCode: z.string().nullable(),
  claimedAmount: z.string().nullable(),
});

/**
 * Something about the match the reviewer must see (#7645). `unbound-match-key`:
 * the supplier configuration's match key names a field no sent 844 line carries,
 * so the claim link matched on the fallback key instead — a configuration to fix.
 */
export const ReviewSessionWarningSchema = z.object({
  kind: z.literal("unbound-match-key"),
  /** The configuration, by the supplier name it carries. */
  configName: z.string(),
  keyFields: z.array(z.string()),
  /** The key fields with no sent-line column. */
  unboundFields: z.array(z.string()),
  /** The key the rows were matched on instead. */
  fallbackKeyFields: z.array(z.string()),
});

/**
 * Why the automatic path left the email for review, when the reviewer must see
 * it (#7646). `unkeyed-report-rows` (owner E18): its ONLY blocker was report
 * rows with no match key at all — typically a totals row. Every other row
 * placed; the reviewer checks those rows.
 *
 * Its own channel, not a {@link ReviewSessionWarningSchema} kind, so the
 * warning shape #7663 shipped stays exactly as it was.
 */
export const ReviewRoutingWarningSchema = z.object({
  kind: z.literal("unkeyed-report-rows"),
  /** The configuration the automatic path settled on, by its label. */
  configName: z.string(),
  /**
   * Each row with no key, by the attachment it came from and its position among
   * the rows the configuration read from THAT attachment (0-based, the order
   * the parse view lists them). `fileName` is null when the attachment is no
   * longer known.
   */
  rows: z.array(
    z.object({
      fileName: z.string().nullable(),
      rowIndex: z.int().nonnegative(),
    }),
  ),
});

export const ReviewSessionOutcomeSchema = z.object({
  applied: z.array(ReviewSessionAppliedSchema),
  recorded: z.array(ReviewSessionRecordedSchema),
  learned: z.array(ReviewSessionLearnedSchema),
  unplaced: z.array(ReviewSessionUnplacedRowSchema),
  warnings: z.array(ReviewSessionWarningSchema),
  /** Present only when the session stated a decision (#7651). */
  statedDecision: ReviewSessionStatedDecisionSchema.optional(),
});

// ── Claim panel (#7645): the supplier's claims, with the engine's suggestions ─

export const SD849ClaimSignalSchema = z.enum(SD_849_CLAIM_SIGNAL_VALUES);

export const ListReviewClaimsInputSchema = z.object({
  inboundEmailId: UUIDSchema,
  /** The supplier on the form — a `co_companies.id` or an `ap_suppliers.id`. */
  supplierId: UUIDSchema,
  /**
   * The attachment roles on the form (#7647). A part stated supporting or
   * irrelevant is not read for report-row evidence; see
   * `sd849ReportAttachmentIds`. Absent reads every attachment.
   */
  attachmentRoles: z
    .array(
      z.object({ attachmentId: UUIDSchema, role: SD849AttachmentRoleSchema }),
    )
    .optional(),
  /**
   * The body's role on the form (#7647). A `payload` body is the report: no
   * attachment left unmarked (a forwarded file included) is read.
   */
  bodyRole: SD849AttachmentRoleSchema.optional(),
});

/**
 * One of the supplier's claims, the way the team talks about it: division,
 * period, VDMs, line count and dollars. `suggested` claims are the engine's
 * candidates, each with the signals that named it; `answered` claims already have
 * an 849 and are still selectable (a re-issued decision lands on them).
 */
export const ReviewClaimSchema = z.object({
  submissionId: UUIDSchema,
  divisionCode: z.string(),
  divisionName: z.string().nullable(),
  period: z.string(),
  vdmNumbers: z.array(z.number().int()),
  /**
   * The batch control numbers the claim's lines were sent under — usually one.
   * Suppliers quote them ("Batch R#30575-R30609"), so the panel lists and finds
   * claims by them.
   */
  batchNumbers: z.array(z.string()).default([]),
  /**
   * The selling locations of the claim's lines, as SENT — the branch names the
   * supplier sees on the 844. A claim batched VDM-first spans several branches,
   * so this is a list; it tells apart claims of one division and period.
   */
  locations: z.array(z.string()).default([]),
  lineCount: z.number().int(),
  claimedAmount: z.string(),
  answered: z.boolean(),
  suggested: z.boolean(),
  signals: z.array(SD849ClaimSignalSchema),
});

/**
 * The decision a supplier states in the body of its reply (SD-33), read from a
 * fixed template — Belden's Completed Claim totals, Hoffman's payback, Superior
 * Essex's approved/denied, Weidmüller's credit total — or recognised as an
 * acknowledgement / request for information, which decides nothing.
 */
export const SD849_BODY_STATED_DECISION_KINDS = [
  "full_acceptance",
  "partial",
  "rejection",
  "payback_request",
  "credit_issued",
  "acknowledgement",
  "information_request",
] as const;

/** The templates read here — named so a hit says which one fired. */
export const SD849_BODY_STATED_DECISION_TEMPLATES = [
  "labelled_totals",
  "payback",
  "approved_denied",
  "credit_issued",
  "non_decision",
  /** The strict single-approval reader ("$X is approved for … VDM N"). */
  "strict_approval",
] as const;

const BodyStatedMoneySchema = z.string().regex(/^\d+\.\d{2}$/);

export const SD849BodyStatedDecisionSchema = z.object({
  kind: z.enum(SD849_BODY_STATED_DECISION_KINDS),
  template: z.enum(SD849_BODY_STATED_DECISION_TEMPLATES),
  /** What the supplier says we claimed, when it restates it. */
  requestedAmount: BodyStatedMoneySchema.nullable(),
  approvedAmount: BodyStatedMoneySchema.nullable(),
  deniedAmount: BodyStatedMoneySchema.nullable(),
  /** What the supplier asks us to pay back — the portion it does not credit. */
  paybackAmount: BodyStatedMoneySchema.nullable(),
  /** The supplier's own credit memo / credit note numbers, as written. */
  creditMemoNumbers: z.array(z.string()),
  /** Our VDM/ADM numbers the reply's own text names, ascending. */
  claimRefs: z.array(z.number().int().positive()),
  /** Why the supplier denied or held part of the claim, in its words. */
  discrepancyReasons: z.array(z.string()),
  /** The text it was read from, for the reviewer to check. */
  evidence: z.string(),
  /** P per field: 1 for everything a template read. */
  p: z.record(z.string(), z.number().min(0).max(1)),
});

export const ReviewClaimsSchema = z.object({
  claims: z.array(ReviewClaimSchema),
  /** More claims exist than were listed; suggestions are always listed. */
  truncated: z.boolean(),
  /** What the reviewer must see about how this supplier's report is matched. */
  warnings: z.array(ReviewSessionWarningSchema),
  /** Why the automatic path left this email for review, when it matters. */
  routingWarnings: z.array(ReviewRoutingWarningSchema).optional(),
  /**
   * The rows the email's last claim link matched to no claim, kept on the email
   * (never on a claim's record, so they count toward no claim).
   */
  notMatchedRows: z.array(ReviewSessionUnplacedRowSchema),
  /**
   * A decision the reply states in its body, with no report to read, that
   * covers the suggested claims exactly: the approved amount equals their
   * claimed total to the cent, and any VDM it names is theirs. Pre-fills the
   * stated decision; the reviewer still confirms it (#7651).
   */
  suggestedStatement: z
    .object({
      intent: z.literal("accepted_in_full"),
      approvedAmount: z.string(),
      vdmNumbers: z.array(z.number().int()),
      sentence: z.string(),
      submissionIds: z.array(UUIDSchema),
    })
    .nullable()
    .optional(),
  /**
   * What the reply's own text states, when it states anything a template reads
   * (SD-33) — shown beside the claims so the reviewer sees the supplier's
   * figures; with no readable report they are the stated decision's checksum.
   */
  bodyStatedDecision: SD849BodyStatedDecisionSchema.nullable().optional(),
});

// ── Parse view (#7647): the eager parse, read back ──────────────────────────

/** Most raw-sheet rows / columns a pre-parse keeps per sheet. */
export const SD_849_PREPARSE_MAX_SHEET_ROWS = 60;
export const SD_849_PREPARSE_MAX_SHEET_COLUMNS = 40;
/** Most parsed rows a pre-parse keeps per (configuration, attachment). */
export const SD_849_PREPARSE_MAX_ROWS = 200;

/** A bounded preview of one worksheet, cells as displayed text, from A1. */
export const PreparseSheetSchema = z.object({
  name: z.string(),
  rows: z.array(z.array(z.string())),
  /** The sheet has more rows or columns than the preview kept. */
  truncated: z.boolean(),
});

export const PreparseAttachmentSchema = z.object({
  /** The document id — the same id the form's attachment roles use. */
  attachmentId: UUIDSchema,
  fileName: z.string(),
  /**
   * Byte-identical copies dropped in favour of this one (a forwarded claim
   * usually carries its workbook twice). A role stated on a copy speaks for
   * this attachment — pass these as `sameContent` to `sd849ReportAttachmentIds`.
   */
  duplicateAttachmentIds: z.array(UUIDSchema).optional(),
  sheets: z.array(PreparseSheetSchema),
});

/** A column a configuration reads, by position in the raw sheet (0 = A). */
export const PreparseMappedColumnSchema = z.object({
  columnIndex: z.int().nonnegative(),
  header: z.string(),
  /** The canonical field it fills (`invoiceNumber`, `supplierCreditAmount`…). */
  field: z.string(),
});

/** One parsed row, projected onto the canonical fields as display text. */
export const PreparseRowSchema = z.object({
  status: z.enum(["accepted", "rejected", "partial"]),
  fields: z.record(z.string(), z.string()),
});

/** What one configuration read from one attachment. */
export const PreparseConfigAttachmentSchema = z.object({
  attachmentId: UUIDSchema,
  rowCount: z.int().nonnegative(),
  sheets: z.array(
    z.object({
      sheetName: z.string(),
      headerRow: z.int().nonnegative(),
      mappedColumns: z.array(PreparseMappedColumnSchema),
    }),
  ),
  /** At most {@link SD_849_PREPARSE_MAX_ROWS}; `rowCount` is the true count. */
  rows: z.array(PreparseRowSchema),
  /** The VDM numbers the rows name — the claim panel's report-row evidence. */
  vdmNumbers: z.array(z.int()),
  /**
   * The claims the rows name themselves, as VDM + batch pairs (SD-79); null
   * when a row states one that is unreadable. Absent on pre-parses stored
   * before it existed.
   */
  claimReferences: z
    .array(
      z.object({
        vdmNumber: z.int(),
        batch: z.object({
          division: z.string(),
          from: z.int(),
          to: z.int(),
        }),
      }),
    )
    .nullable()
    .optional(),
  /** The attachment could not be read with this configuration, and why. */
  error: z.string().nullable(),
});

/** One configuration's dry run over the email, keyed by its row and version. */
export const PreparseConfigurationSchema = z.object({
  configId: UUIDSchema,
  label: z.string(),
  /** The configuration version that ran; null when it has no recorded history. */
  versionNumber: z.int().positive().nullable(),
  /** The configuration names at least one sheet to read. */
  hasSheets: z.boolean(),
  rowCount: z.int().nonnegative(),
  attachments: z.array(PreparseConfigAttachmentSchema),
});

/**
 * The stored pre-parse (#7647): every configuration of the supplier, tried in
 * their current order over every Excel attachment, as a dry run that wrote
 * nothing. `usedConfigId` is the one the engine would read the report with.
 */
export const EmailPreparseResultSchema = z.object({
  attachments: z.array(PreparseAttachmentSchema),
  configurations: z.array(PreparseConfigurationSchema),
  usedConfigId: UUIDSchema.nullable(),
});

export const SD_849_PREPARSE_STATUSES = [
  "parsed",
  "no_configuration",
  "no_report_attachments",
] as const;

/** One saved version of a configuration — the history the parse view shows. */
export const PreparseConfigVersionSchema = z.object({
  versionNumber: z.int().positive(),
  changeReason: z.string().nullable(),
  status: z.string(),
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
});

/** A configuration as it stands now, with its history, for the parse view. */
export const PreparseConfigInfoSchema = z.object({
  configId: UUIDSchema,
  supplierName: z.string(),
  label: z.string(),
  /** Null when the configuration was deleted since the parse. */
  status: z.string().nullable(),
  history: z.array(PreparseConfigVersionSchema),
});

export const EmailPreparseSchema = z.object({
  supplierId: UUIDSchema.nullable(),
  supplierName: z.string().nullable(),
  status: z.enum(SD_849_PREPARSE_STATUSES),
  parsedAt: z.coerce.date(),
  result: EmailPreparseResultSchema,
  configInfo: z.array(PreparseConfigInfoSchema),
  /**
   * Set when these rows no longer describe what a claim link would read: the
   * supplier's configurations changed since the parse and re-parsing failed
   * (`refresh_failed`). The rows are the earlier parse's; a claim link reads the
   * report again with the current configurations. Null when current.
   */
  stale: z.object({ reason: z.enum(["refresh_failed"]) }).nullable(),
  /**
   * The configuration the automatic path settled on when it left this email
   * for review (#7646): the working one, else the best partial (most rows
   * placed). The parse view opens on it. Absent when the path never ran.
   */
  routedConfigId: UUIDSchema.nullable().optional(),
});

export const GetEmailPreparseOutputSchema = z.object({
  /** Null when the email was never pre-parsed. */
  preparse: EmailPreparseSchema.nullable(),
});

// ── Parse fix preview (#7649) ──────────────────────────────────────────────

export const PreviewParseFixInputSchema = z.object({
  inboundEmailId: UUIDSchema,
  /** The supplier on the form — a `co_companies.id` or an `ap_suppliers.id`. */
  supplierId: UUIDSchema,
  /** The configuration being corrected. */
  baseConfigId: UUIDSchema,
  fix: ParseFixRequestSchema,
  /** The claims ticked in the claim panel: the dry run places rows on them. */
  submissionIds: z.array(UUIDSchema).max(SD_849_REVIEW_MAX_CLAIMS).optional(),
  attachmentRoles: z
    .array(
      z.object({ attachmentId: UUIDSchema, role: SD849AttachmentRoleSchema }),
    )
    .optional(),
  /** The body's stated role; a payload body reads no unmarked attachment (#7647). */
  bodyRole: SD849AttachmentRoleSchema.optional(),
});

/** Why a parse fix cannot become a configuration as it stands. */
export const ParseFixViolationSchema = z.discriminatedUnion("kind", [
  /** The patch names a sheet the configuration does not have. */
  z.object({ kind: z.literal("no_such_sheet") }),
  /** No sheet of the configuration reads the picked worksheet. */
  z.object({ kind: z.literal("sheet_not_read"), sheetName: z.string() }),
  /** The picked column has no header text to read it by. */
  z.object({
    kind: z.literal("header_empty"),
    field: SD849ParseFixFieldSchema,
  }),
  /** Several columns carry that header: reading it by name is ambiguous. */
  z.object({
    kind: z.literal("header_not_unique"),
    field: SD849ParseFixFieldSchema,
    header: z.string(),
  }),
  /** The fix changes nothing the configuration reads. */
  z.object({ kind: z.literal("no_change") }),
  /** The result fails the configuration schema. */
  z.object({
    kind: z.literal("invalid_configuration"),
    errors: z.array(z.string()),
  }),
  /** The drafting agent could not turn the text into a fix; `explanation` says why. */
  z.object({ kind: z.literal("agent_declined") }),
  /** The drafting agent could not be reached. */
  z.object({ kind: z.literal("agent_unavailable") }),
  /** The fixed configuration reads no row of this email. */
  z.object({ kind: z.literal("reads_no_rows") }),
  /** The fixed configuration places none of its rows on the linked claims. */
  z.object({ kind: z.literal("places_no_rows") }),
]);

/** One configuration's dry run on the email: what it reads, and what it places. */
export const ParseFixDryRunSchema = z.object({
  rowCount: z.int().nonnegative(),
  attachments: z.array(PreparseConfigAttachmentSchema),
  /** On the ticked claims; null when none is ticked. */
  placement: z
    .object({
      placedCount: z.int().nonnegative(),
      failedCount: z.int().nonnegative(),
    })
    .nullable(),
});

/**
 * What a parse fix WOULD produce, before it enters the ranking: the patch, the
 * drafting agent's explanation (plain-text fixes), anything that stops it, and
 * the dry run of the configuration it corrects beside the fixed one — both on
 * this same email. READ-ONLY: nothing is saved and no line is decided.
 */
export const ParseFixPreviewSchema = z.object({
  patch: Sd849SheetPatchSchema.nullable(),
  explanation: z.string().nullable(),
  violations: z.array(ParseFixViolationSchema),
  base: ParseFixDryRunSchema,
  fixed: ParseFixDryRunSchema.nullable(),
});

// ── Browse mode: the whole mailbox, labeled and unlabeled ───────────────────

/** Which slice of the mailbox the browse list shows. */
export const LabelingMailboxFilterSchema = z.enum([
  "unlabeled",
  "labeled",
  "all",
]);

/** An email's current label state — null when nobody has labeled it yet. */
export const LabelingMailboxLabelSchema = z.object({
  disposition: SD849DispositionSchema.nullable(),
  /** Set only once the expert got as far as naming a supplier. */
  supplierId: UUIDSchema.nullable(),
  labeledAt: z.coerce.date(),
  labeledByUserId: UUIDSchema.nullable(),
  /**
   * Whether this counts as fully labeled, by the same rule the queue uses.
   *
   * An `is_849` with a missing or stale supplier has a disposition but is NOT
   * complete. The status column must reflect that, or it shows finished work while
   * the Unlabeled filter still lists the row — and the two disagreeing is worse
   * than either being wrong on its own.
   */
  isComplete: z.boolean(),
});

export const LabelingMailboxItemSchema = z.object({
  inboundEmailId: UUIDSchema,
  from: z.string(),
  subject: z.string().nullable(),
  receivedAt: z.coerce.date(),
  attachmentCount: z.int().nonnegative(),
  detectedSupplier: DetectedSupplierSchema,
  confidence: z.object({
    isAn849: z.number().min(0).max(1),
    relatedToExisting: z.number().min(0).max(1),
    signals: z.array(Email849ScoreSignalSchema),
  }),
  /** Every 849 this email is on — one per claim it answers — oldest first. */
  existingResponseIds: z.array(UUIDSchema),
  label: LabelingMailboxLabelSchema.nullable(),
  /** The classifier's stored verdict — null when the email was never classified. */
  classification: LabelingClassificationSchema.nullable(),
});

// ── Shared ──────────────────────────────────────────────────────────────────

export const LabelingCountsSchema = z.object({
  /**
   * Unlabelled mailbox emails — everything a person COULD label.
   *
   * Not a queue, which is why it is no longer called one: the Review queue is what
   * the classifier escalated, this is the whole unlabelled corpus.
   */
  unlabeledEmails: z.int().nonnegative(),
  /** Members of the Review queue: what the classifier escalated, before the cap. */
  reviewQueue: z.int().nonnegative(),
  labeledToday: z.int().nonnegative(),
});

export const LabeledRecordItemSchema = z.object({
  labelId: UUIDSchema,
  kind: LabelingQueueKindSchema,
  subjectId: z.string(),
  value: z.string(),
  /** Human-readable context (email subject, or supplier + status). */
  label: z.string(),
  labeledByUserId: UUIDSchema.nullable(),
  /**
   * Who labelled it, resolved for display.
   *
   * The id alone is unreadable, and this page exists precisely to answer "who
   * decided this" — a label store that is per-labeler by construction (one live row
   * per user per subject) is only auditable if the reviewer is named. Null for a
   * system/agent label, or a user row that no longer resolves.
   */
  labeledByName: z.string().nullable(),
  labeledAt: z.coerce.date(),
  /** Whether the expert agreed with what the engine predicted. */
  agreedWithEngine: z.boolean().nullable(),
  relabelled: z.boolean(),
});

const PageInputSchema = z.object({
  page: z.int().min(1).optional(),
  pageSize: z.int().min(1).max(100).optional(),
});

/**
 * The Review queue — replaces the old focus mode.
 *
 * NOT paginated, and that is the design: the queue is capped precisely so it has a
 * visible bottom, and a cap you can page past is not a cap. `totalCount` is the true
 * number of members so the page can say what the cap is withholding rather than
 * pretending the queue is shorter than it is. The cap itself stays server-side —
 * what the reviewer needs is how many rows are on screen and how many there are,
 * and the page has both.
 */
const listReviewQueueContract = oc
  .input(
    z.object({
      /** Emails the reviewer skipped this session — "not now", not a label. */
      skipInboundEmailIds: z.array(UUIDSchema).optional(),
    }),
  )
  .output(
    z.object({
      items: z.array(LabelingReviewQueueItemSchema),
      /** Members of the queue BEFORE the cap. */
      totalCount: z.int().nonnegative(),
    }),
  )
  .route({
    method: "GET",
    summary: "List 849 emails awaiting review",
    description:
      "Lists inbound ship and debit response emails the classifier escalated for human review, likeliest first, capped, with the true total. Optionally skips emails the reviewer passed over.",
  });

const listMailboxContract = oc
  .input(
    PageInputSchema.extend({
      search: z.string().optional(),
      filter: LabelingMailboxFilterSchema.optional(),
    }),
  )
  .output(
    z.object({
      items: z.array(LabelingMailboxItemSchema),
      totalCount: z.int().nonnegative(),
      totalPages: z.int().nonnegative(),
    }),
  )
  .route({
    method: "GET",
    summary: "Browse the ship and debit mailbox",
    description:
      "Pages through inbound ship and debit emails with search and an unlabeled, labeled, or all filter, showing each email's current label state.",
  });

const getEmailForLabelingContract = oc
  .input(z.object({ inboundEmailId: UUIDSchema }))
  .output(LabelingEmailItemSchema)
  .route({
    method: "GET",
    summary: "Get an email for labeling",
    description:
      "Returns one inbound 849 email's labeling context: pre-filled answers from the classifier, detected supplier, and its attachments.",
  });

const listLabeledContract = oc
  .input(PageInputSchema.extend({ kind: LabelingQueueKindSchema }))
  .output(
    z.object({
      items: z.array(LabeledRecordItemSchema),
      totalCount: z.int().nonnegative(),
      totalPages: z.int().nonnegative(),
    }),
  )
  .route({
    method: "GET",
    summary: "List labeled 849 emails or matches",
    description:
      "Pages through what reviewers have already labeled, either emails or matches, showing the decisions they recorded.",
  });

// ── Review surface visibility (#7655) ─────────────────────────────────────

const ReviewQueueTotalSchema = z.object({
  count: z.int().nonnegative(),
  /** Decimal string, exact to the cent. */
  claimedAmount: z.string(),
});

/**
 * Counts and dollars per queue for the review surface's header. To classify +
 * to link = the Review queue's depth; unplaced rows are rows a claim link could
 * not place. Dollars for the email queues are the outstanding claimed amount on
 * the claims the engine's stored attempt named (each claim once per queue).
 */
export const ReviewQueueTotalsSchema = z.object({
  toClassify: ReviewQueueTotalSchema,
  toLink: ReviewQueueTotalSchema,
  unplacedRows: ReviewQueueTotalSchema,
});

const ClaimAgingCellSchema = z.object({
  claimCount: z.int().nonnegative(),
  lineCount: z.int().nonnegative(),
  /** Decimal string, exact to the cent. */
  claimedAmount: z.string(),
});

const ClaimAgingBucketRowSchema = z.object({
  bucket: z.enum(SD_849_CLAIM_AGE_BUCKETS),
  noResponse: ClaimAgingCellSchema,
  responseNotApplied: ClaimAgingCellSchema,
});

const ClaimAgingSummarySchema = z.object({
  buckets: z.array(ClaimAgingBucketRowSchema),
  noResponse: ClaimAgingCellSchema,
  responseNotApplied: ClaimAgingCellSchema,
  /**
   * Lines in `edited_for_resubmission`, all ages: the claim is being re-sent,
   * so they are NOT outstanding and are in neither half above.
   */
  resubmitting: ClaimAgingCellSchema,
});

/**
 * Outstanding email-claim lines per supplier by age since the claim was sent,
 * split into "no response received" (chase the supplier) and "response
 * received, not applied" (work the review queue); lines being resubmitted are
 * a separate figure, not outstanding.
 */
export const ClaimAgingSchema = z.object({
  asOf: z.coerce.date(),
  suppliers: z.array(
    ClaimAgingSummarySchema.extend({
      companyId: UUIDSchema,
      supplierName: z.string(),
    }),
  ),
  totals: ClaimAgingSummarySchema,
});

const reviewQueueTotalsContract = oc
  .input(z.object({}))
  .output(ReviewQueueTotalsSchema)
  .route({
    method: "GET",
    summary: "Get 849 review queue totals",
    description:
      "Returns the count and outstanding claimed dollars for the to-classify, to-link, and unplaced-rows review queues.",
  });

const claimAgingContract = oc
  .input(
    z.object({
      /** Measure ages as of this instant (default now) — re-measuring a snapshot. */
      asOf: z.coerce.date().optional(),
    }),
  )
  .output(ClaimAgingSchema)
  .route({
    method: "GET",
    summary: "Get outstanding claim aging by supplier",
    description:
      "Buckets outstanding email-claim lines per supplier by age since sent, split into no response yet versus response not applied; resubmitting lines shown separately. Optional as-of date.",
  });

const countsContract = oc
  .input(z.object({}))
  .output(LabelingCountsSchema)
  .route({
    method: "GET",
    summary: "Get labeling queue counts",
    description:
      "Returns the number of unlabeled mailbox emails, emails in the review queue, and labels recorded today.",
  });

/**
 * The review session: the one write that answers an email — its labels and
 * every correction (#7644, #7657). There is no label-only submit.
 */
const submitReviewSessionContract = oc
  .input(SubmitReviewSessionInputSchema)
  .output(ReviewSessionOutcomeSchema)
  .route({
    method: "POST",
    summary: "Submit an 849 email review",
    description:
      "Records the reviewer's labels for one email and applies corrections: re-points the supplier, links claims and decides their lines, saves parse fixes as new configurations.",
  });

export const SaveReviewSupplierInputSchema = z.object({
  inboundEmailId: UUIDSchema,
  /** The picked supplier — a `co_companies.id` from the picker. */
  supplierId: UUIDSchema,
});

export const SaveReviewSupplierOutputSchema = z.object({
  /** The `ap_suppliers.id` the label was recorded under. */
  supplierId: UUIDSchema,
  supplierName: z.string(),
});

/**
 * The supplier answer on its own, saved the moment it is picked — the reviewer
 * never has to submit to keep it. The same correction the session's submit
 * applies (a label; it re-points no record), so a later submit just confirms it.
 */
const saveReviewSupplierContract = oc
  .input(SaveReviewSupplierInputSchema)
  .output(SaveReviewSupplierOutputSchema)
  .route({
    method: "POST",
    summary: "Save the supplier for an 849 email",
    description:
      "Records the reviewer's supplier pick for one 849 email as a label immediately, without submitting the review; it re-points no existing record.",
  });

export const SaveReviewNoteInputSchema = z.object({
  inboundEmailId: UUIDSchema,
  note: z.string().trim().min(1).max(SD_849_REVIEW_NOTE_MAX_LENGTH),
});

export const SaveReviewNoteOutputSchema = z.object({
  /** The note as stored, trimmed. */
  note: z.string(),
});

/**
 * The reviewer's note on a verdict they already submitted (AIA-38) — asked
 * after a submit that overturned a confident classifier, so the form stays
 * blind to it. Added to their own verdict label; the verdict is unchanged.
 */
const saveReviewNoteContract = oc
  .input(SaveReviewNoteInputSchema)
  .output(SaveReviewNoteOutputSchema)
  .route({
    method: "POST",
    summary: "Add a note to an 849 review verdict",
    description:
      "Adds the reviewer's note to the verdict label they already submitted for one 849 email; the verdict itself is unchanged.",
  });

/**
 * The claim panel (#7645): the supplier's claims for one email, the engine's
 * candidates pre-ticked with where each was read. READ-ONLY.
 */
const listReviewClaimsContract = oc
  .input(ListReviewClaimsInputSchema)
  .output(ReviewClaimsSchema)
  .route({
    method: "GET",
    summary: "List supplier claims for an 849 email",
    description:
      "Read-only. Lists the supplier's ship and debit claims for one email, with the engine's suggested matches pre-ticked and where each was found.",
  });

/**
 * The stated-decision preview (#7651): what a statement would decide on the
 * linked claims and how it checks against the email's totals — shown before the
 * reviewer confirms. READ-ONLY.
 */
const previewStatedDecisionContract = oc
  .input(PreviewStatedDecisionInputSchema)
  .output(StatedDecisionPreviewSchema)
  .route({
    method: "POST",
    summary: "Preview a stated 849 decision",
    description:
      "Read-only. Shows what a reviewer's stated decision would decide on the linked claim lines and how it checks against the email's totals.",
  });

/**
 * The rejection-detail preview (SD-10): which workbook rows land on which EDI
 * 849 lines, and which do not and why. READ-ONLY — the attach is applied by
 * `submitReviewSession`.
 */
const previewRejectionDetailContract = oc
  .input(PreviewRejectionDetailInputSchema)
  .output(RejectionDetailPreviewSchema)
  .route({
    method: "POST",
    summary: "Preview where a supplier email's rejection detail lands",
    description:
      "Read-only. For a supplier whose emails only add rejection detail to its EDI 849s, shows which workbook rows land on which EDI 849 lines and which rows are left unplaced and why, or why no target resolved.",
  });

/**
 * The parse fix preview (#7649): a column pick or a plain-text fix turned into a
 * configuration and dry-run on the same email, beside the configuration it
 * corrects. READ-ONLY — the fix enters the ranking only through
 * `submitReviewSession`.
 */
const previewParseFixContract = oc
  .input(PreviewParseFixInputSchema)
  .output(ParseFixPreviewSchema)
  .route({
    method: "POST",
    summary: "Preview a parse fix for an 849 email",
    description:
      "Read-only. Turns a column pick or plain-text fix into a configuration and dry-runs it on the email beside the configuration it corrects.",
  });

/**
 * The parse view on ticked claims (#7648): the configuration the claim link
 * will apply there. READ-ONLY — reads the report and places it without writing.
 */
const previewClaimLinkConfigurationContract = oc
  .input(PreviewClaimLinkConfigurationInputSchema)
  .output(ClaimLinkConfigurationSchema)
  .route({
    method: "POST",
    summary: "Preview the config a claim link applies",
    description:
      "Read-only. Shows which 849 parsing configuration linking the ticked claims would apply, and how it reads and places the email's report rows.",
  });

/**
 * The parse view (#7647): the email's stored pre-parse and the history of the
 * configurations it ran. Opening the review modal parses nothing; picking a
 * different supplier on the form re-parses under that supplier.
 */
const getEmailPreparseContract = oc
  .input(
    z.object({
      inboundEmailId: UUIDSchema,
      /**
       * The supplier on the form. When it differs from the one the stored
       * parse ran under, the email is parsed again under it — a dry run that
       * decides no line.
       */
      supplierId: UUIDSchema.optional(),
    }),
  )
  .output(GetEmailPreparseOutputSchema)
  .route({
    method: "GET",
    summary: "Get an 849 email's parse results",
    description:
      "Returns the email's stored pre-parse and the configurations it ran. Passing a different supplier re-parses as a dry run that decides no lines.",
  });

/**
 * The files inside a zip attached to an 849 email. READ-ONLY — opening the zip
 * on the review page lists them so the reviewer can pick the related ones.
 */
const listEmailArchiveMembersContract = oc
  .input(EmailArchiveTargetSchema)
  .output(EmailArchiveListingSchema)
  .route({
    method: "GET",
    summary: "List files inside an 849 email zip",
    description:
      "Read-only. Lists the files inside a zip attachment on an 849 email, and why any cannot be extracted, so the reviewer can pick related ones.",
  });

/**
 * Take the picked files out of a zip and attach each one to the email, so the
 * parse, the parse view and the claim link read them like any attachment.
 * Idempotent: a file taken out before is returned, not copied again.
 */
const extractEmailArchiveMembersContract = oc
  .input(ExtractEmailArchiveMembersInputSchema)
  .output(ExtractEmailArchiveMembersOutputSchema)
  .route({
    method: "POST",
    summary: "Extract zip files onto an 849 email",
    description:
      "Takes the picked files out of a zip attachment and attaches each to the email so parsing and claim linking read them. Idempotent.",
  });

const undoContract = oc
  .input(z.object({ kind: LabelingQueueKindSchema, subjectId: z.string() }))
  .output(z.object({ removed: z.int().nonnegative() }))
  .route({
    method: "POST",
    summary: "Undo my label on an email or match",
    description:
      "Deletes the current user's own labels on one email or match, returning it to its labeling queue. Returns how many labels were removed.",
  });

// ── Review-queue exclusion rules ────────────────────────────────────────────

/** Matches `REVIEW_EXCLUSION_PATTERN_MAX_LENGTH` and the column width. */
const ExclusionPatternSchema = z.string().max(255).nullable().optional();

/**
 * A hand-written rule keeping emails out of the review queue: the sender and/or
 * subject patterns it matches (case-insensitive "contains", `*` a wildcard).
 */
export const ReviewExclusionRuleSchema = z.object({
  id: UUIDSchema,
  senderPattern: z.string().nullable(),
  subjectPattern: z.string().nullable(),
  note: z.string().nullable(),
  createdByName: z.string().nullable(),
  createdAt: z.coerce.date(),
});

/**
 * A rule somebody removed. It no longer hides anything; it is kept so the
 * dialog can answer "who took that rule away, and when".
 */
export const RemovedReviewExclusionRuleSchema =
  ReviewExclusionRuleSchema.extend({
    deletedByName: z.string().nullable(),
    deletedAt: z.coerce.date(),
  });

const ReviewExclusionRulePatternsInputSchema = z.object({
  senderPattern: ExclusionPatternSchema,
  subjectPattern: ExclusionPatternSchema,
});

export const CreateReviewExclusionRuleInputSchema =
  ReviewExclusionRulePatternsInputSchema.extend({
    note: z.string().max(500).nullable().optional(),
  });

/** What a rule being typed would take out of the queue as it stands now. */
export const ReviewExclusionRulePreviewSchema = z.object({
  queueMatches: z.int().nonnegative(),
  examples: z.array(
    z.object({
      inboundEmailId: UUIDSchema,
      from: z.string().nullable(),
      subject: z.string().nullable(),
    }),
  ),
});

const listReviewExclusionRulesContract = oc
  .input(z.object({}))
  .output(
    z.object({
      rules: z.array(ReviewExclusionRuleSchema),
      removed: z.array(RemovedReviewExclusionRuleSchema),
    }),
  )
  .route({
    method: "GET",
    summary: "List 849 review queue exclusion rules",
    description:
      "Lists the sender and subject pattern rules that keep matching emails out of the 849 review queue, with notes and who created them, plus the rules since removed and by whom. Requires S&D administration access.",
  });

const createReviewExclusionRuleContract = oc
  .input(CreateReviewExclusionRuleInputSchema)
  .output(z.object({ id: UUIDSchema }))
  .route({
    method: "POST",
    summary: "Create an 849 review queue exclusion rule",
    description:
      "Adds a sender and/or subject pattern rule (case-insensitive contains, * wildcard) that keeps matching emails out of the review queue. Changes no email, label, or 849. Requires S&D administration access.",
  });

const deleteReviewExclusionRuleContract = oc
  .input(z.object({ ruleId: UUIDSchema }))
  .output(z.object({ removed: z.literal(true) }))
  .route({
    method: "POST",
    summary: "Delete an 849 review queue exclusion rule",
    description:
      "Removes one exclusion rule by id, recording who removed it, so emails it hid can return to the 849 review queue. Requires S&D administration access.",
  });

const previewReviewExclusionRuleContract = oc
  .input(ReviewExclusionRulePatternsInputSchema)
  .output(ReviewExclusionRulePreviewSchema)
  .route({
    method: "POST",
    summary: "Preview an 849 review exclusion rule",
    description:
      "Read-only. Counts which emails currently in the review queue a sender or subject pattern would hide, with example emails. Requires S&D administration access.",
  });

export const labelingContracts = {
  listReviewQueue: listReviewQueueContract,
  listMailbox: listMailboxContract,
  getEmailForLabeling: getEmailForLabelingContract,
  listLabeled: listLabeledContract,
  counts: countsContract,
  reviewQueueTotals: reviewQueueTotalsContract,
  claimAging: claimAgingContract,
  submitReviewSession: submitReviewSessionContract,
  saveReviewSupplier: saveReviewSupplierContract,
  saveReviewNote: saveReviewNoteContract,
  listReviewClaims: listReviewClaimsContract,
  previewStatedDecision: previewStatedDecisionContract,
  previewRejectionDetail: previewRejectionDetailContract,
  getEmailPreparse: getEmailPreparseContract,
  previewParseFix: previewParseFixContract,
  previewClaimLinkConfiguration: previewClaimLinkConfigurationContract,
  listEmailArchiveMembers: listEmailArchiveMembersContract,
  extractEmailArchiveMembers: extractEmailArchiveMembersContract,
  undo: undoContract,
  listReviewExclusionRules: listReviewExclusionRulesContract,
  createReviewExclusionRule: createReviewExclusionRuleContract,
  deleteReviewExclusionRule: deleteReviewExclusionRuleContract,
  previewReviewExclusionRule: previewReviewExclusionRuleContract,
};

export type ReviewExclusionRule = z.infer<typeof ReviewExclusionRuleSchema>;
export type RemovedReviewExclusionRule = z.infer<
  typeof RemovedReviewExclusionRuleSchema
>;
export type ReviewExclusionRulePreview = z.infer<
  typeof ReviewExclusionRulePreviewSchema
>;
export type CreateReviewExclusionRuleInput = z.infer<
  typeof CreateReviewExclusionRuleInputSchema
>;
export type LabelingQueueKind = z.infer<typeof LabelingQueueKindSchema>;
export type LabelingMailboxFilter = z.infer<typeof LabelingMailboxFilterSchema>;
export type LabelingMailboxItem = z.infer<typeof LabelingMailboxItemSchema>;
export type LabelingClassification = z.infer<
  typeof LabelingClassificationSchema
>;
export type SD849AttachmentRoleValue = z.infer<
  typeof SD849AttachmentRoleSchema
>;
export type SD849SignalValue = z.infer<typeof SD849SignalSchema>;
export type SD849DispositionValue = z.infer<typeof SD849DispositionSchema>;
export type LabelingEmailItem = z.infer<typeof LabelingEmailItemSchema>;
export type SaveReviewSupplierInput = z.infer<
  typeof SaveReviewSupplierInputSchema
>;
export type LabelingReviewQueueItem = z.infer<
  typeof LabelingReviewQueueItemSchema
>;
export type LabelingAttachment = z.infer<typeof LabelingAttachmentSchema>;
export type LabelingMailboxCopy = z.infer<typeof LabelingMailboxCopySchema>;
export type SD849ArchiveMemberUnavailableReason = z.infer<
  typeof SD849ArchiveMemberUnavailableReasonSchema
>;
export type EmailArchiveMember = z.infer<typeof EmailArchiveMemberSchema>;
export type EmailArchiveListing = z.infer<typeof EmailArchiveListingSchema>;
export type ExtractEmailArchiveMembersInput = z.infer<
  typeof ExtractEmailArchiveMembersInputSchema
>;
export type ExtractEmailArchiveMembersOutput = z.infer<
  typeof ExtractEmailArchiveMembersOutputSchema
>;
export type LabelingCounts = z.infer<typeof LabelingCountsSchema>;
export type ReviewQueueTotals = z.infer<typeof ReviewQueueTotalsSchema>;
export type ClaimAging = z.infer<typeof ClaimAgingSchema>;
export type LabeledRecordItem = z.infer<typeof LabeledRecordItemSchema>;
export type SubmitEmailLabelInput = z.infer<typeof SubmitEmailLabelInputSchema>;
export type SubmitReviewSessionInput = z.infer<
  typeof SubmitReviewSessionInputSchema
>;
export type SD849ReviewReason = z.infer<typeof SD849ReviewReasonSchema>;
export type ReviewSessionOutcome = z.infer<typeof ReviewSessionOutcomeSchema>;
export type ReviewSessionLearned = z.infer<typeof ReviewSessionLearnedSchema>;
export type ReviewSessionApplied = z.infer<typeof ReviewSessionAppliedSchema>;
export type ReviewSessionRecorded = z.infer<typeof ReviewSessionRecordedSchema>;
export type ReviewSessionUnplacedRow = z.infer<
  typeof ReviewSessionUnplacedRowSchema
>;
export type SD849UnplacedReason = z.infer<typeof SD849UnplacedReasonSchema>;
export type ReviewSessionWarning = z.infer<typeof ReviewSessionWarningSchema>;
export type ReviewSessionStatedDecision = z.infer<
  typeof ReviewSessionStatedDecisionSchema
>;
export type ReviewRoutingWarning = z.infer<typeof ReviewRoutingWarningSchema>;
export type SD849ClaimSignal = z.infer<typeof SD849ClaimSignalSchema>;
export type ReviewClaim = z.infer<typeof ReviewClaimSchema>;
export type ReviewClaims = z.infer<typeof ReviewClaimsSchema>;
export type ListReviewClaimsInput = z.infer<typeof ListReviewClaimsInputSchema>;
export type SD849StatedIntent = z.infer<typeof SD849StatedIntentSchema>;
export type StatedDecisionInput = z.input<typeof StatedDecisionInputSchema>;
export type StatedLineCredit = z.infer<typeof StatedLineCreditSchema>;
export type StatedChecksum = z.infer<typeof StatedChecksumSchema>;
export type SD849BodyStatedDecision = z.infer<
  typeof SD849BodyStatedDecisionSchema
>;
export type StatedChecksumTotals = z.infer<typeof StatedChecksumTotalsSchema>;
export type StatedDecisionStatement = z.infer<typeof StatedDecisionInputSchema>;
export type StatedLineSkipContract = z.infer<typeof StatedLineSkipSchema>;
export type StatedLineDecisionContract = z.infer<
  typeof StatedLineDecisionSchema
>;
export type StatedDecisionViolationContract = z.infer<
  typeof StatedDecisionViolationSchema
>;
export type StatedClaimLineContract = z.infer<typeof StatedClaimLineSchema>;
export type PreviewStatedDecisionInput = z.input<
  typeof PreviewStatedDecisionInputSchema
>;
export type StatedDecisionPreview = z.infer<typeof StatedDecisionPreviewSchema>;
export type PreviewRejectionDetailInput = z.infer<
  typeof PreviewRejectionDetailInputSchema
>;
export type RejectionDetailPreview = z.infer<
  typeof RejectionDetailPreviewSchema
>;
export type PreviewClaimLinkConfigurationInput = z.input<
  typeof PreviewClaimLinkConfigurationInputSchema
>;
export type ClaimLinkConfiguration = z.infer<
  typeof ClaimLinkConfigurationSchema
>["configuration"];
export type EmailPreparse = z.infer<typeof EmailPreparseSchema>;
export type EmailPreparseResult = z.infer<typeof EmailPreparseResultSchema>;
export type PreparseConfiguration = z.infer<typeof PreparseConfigurationSchema>;
export type PreparseConfigAttachment = z.infer<
  typeof PreparseConfigAttachmentSchema
>;
export type PreparseAttachment = z.infer<typeof PreparseAttachmentSchema>;
export type PreparseMappedColumn = z.infer<typeof PreparseMappedColumnSchema>;
export type PreparseRow = z.infer<typeof PreparseRowSchema>;
export type Sd849SheetPatch = z.infer<typeof Sd849SheetPatchSchema>;
export type ParseColumnPick = z.infer<typeof ParseColumnPickSchema>;
export type ParseFixRequest = z.infer<typeof ParseFixRequestSchema>;
export type SessionParseFix = z.infer<typeof SessionParseFixSchema>;
export type PreviewParseFixInput = z.infer<typeof PreviewParseFixInputSchema>;
export type ParseFixViolation = z.infer<typeof ParseFixViolationSchema>;
export type ParseFixDryRun = z.infer<typeof ParseFixDryRunSchema>;
export type ParseFixPreview = z.infer<typeof ParseFixPreviewSchema>;
export type PreparseConfigInfo = z.infer<typeof PreparseConfigInfoSchema>;
export type Sd849PreparseStatus = (typeof SD_849_PREPARSE_STATUSES)[number];
export type LearnedSenderStatus = z.infer<typeof LearnedSenderStatusSchema>;
export type SD849DispositionSource = z.infer<
  typeof SD849DispositionSourceSchema
>;
export type SD849BodyRoleSource = z.infer<typeof SD849BodyRoleSourceSchema>;
export type SD849AttachmentRoleSource = z.infer<
  typeof SD849AttachmentRoleSourceSchema
>;
export type SD849SupplierSource = z.infer<typeof SD849SupplierSourceSchema>;
